/**
 * FileManager Component
 * Upload folders, list, and download files for a subject/visit
 */

import { useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Trash2, File, Image, FileText, Archive, X, Loader2, FolderUp, ChevronDown, ChevronRight, Plus, ShieldAlert } from 'lucide-react';
import { filesApi, FileUpload } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface FileManagerProps {
  subjectId: string;
  visitId?: string;
  studyCode: string;
  readOnly?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: 'angiography', label: 'Angiography' },
  { value: 'oct', label: 'OCT' },
  { value: 'ivus', label: 'IVUS' },
  { value: 'tws', label: 'TWS' },
  { value: 'other', label: 'Other' },
];

const CONTEXT_OPTIONS = [
  { value: 'PROC', label: 'Procedure' },
  { value: 'AE', label: 'Adverse Event' },
  { value: 'FU30', label: 'Follow-up (30-Day)' },
  { value: 'FU6M', label: 'Follow-up (6-Month)' },
  { value: 'FU1Y', label: 'Follow-up (1-Year)' },
  { value: 'FU2Y', label: 'Follow-up (2-Year)' },
  { value: 'FU3Y', label: 'Follow-up (3-Year)' },
  { value: 'SCREEN', label: 'Screening' },
  { value: 'OTHER', label: 'Other' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'image': return <Image className="w-4 h-4 text-blue-500" />;
    case 'pdf': return <FileText className="w-4 h-4 text-red-500" />;
    case 'zip': return <Archive className="w-4 h-4 text-yellow-600" />;
    case 'dicom': return <File className="w-4 h-4 text-purple-500" />;
    default: return <File className="w-4 h-4 text-gray-500" />;
  }
}

export default function FileManager({ subjectId, visitId, studyCode: _studyCode, readOnly }: FileManagerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDocsFollowUp, setShowDocsFollowUp] = useState(false);
  const [lastUploadContext, setLastUploadContext] = useState('PROC');
  const [fileContext, setFileContext] = useState('PROC');
  const [category, setCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);
  const [downloadingGroup, setDownloadingGroup] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<Record<string, 'images' | 'documents'>>({});
  // For adding docs to an existing group
  const [addDocsToGroup, setAddDocsToGroup] = useState<string | null>(null);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [docCategory, setDocCategory] = useState('tws');
  const [docDescription, setDocDescription] = useState('');
  const [docUploadProgress, setDocUploadProgress] = useState<{ current: number; total: number; name: string } | null>(null);
  // Top-level individual document upload (separate from the image-folder flow)
  const [showDocsTopLevel, setShowDocsTopLevel] = useState(false);
  const [topDocContext, setTopDocContext] = useState('PROC');
  const folderInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const followUpDocInputRef = useRef<HTMLInputElement>(null);
  const topDocInputRef = useRef<HTMLInputElement>(null);

  const canUpload = !readOnly && (user?.role === 'admin' || user?.role === 'site-rc' || user?.role === 'site');
  const canDelete = (file: FileUpload) => {
    if (user?.role === 'admin') return true;
    // Uploaders can delete their own files
    if (file.uploadedBy?.username === user?.username) return true;
    return false;
  };

  // Fetch files
  const filters: Record<string, string> = { subjectId };
  if (visitId) filters.visitId = visitId;

  const { data, isLoading } = useQuery({
    queryKey: ['files', subjectId, visitId],
    queryFn: () => filesApi.list(filters),
  });

  const files = data?.files || [];

  // Group files by context (visitId)
  const fileGroups = useMemo(() => {
    const groups: { context: string; label: string; files: FileUpload[] }[] = [];
    const grouped = new Map<string, FileUpload[]>();

    for (const file of files) {
      const ctx = file.visitId || 'OTHER';
      if (!grouped.has(ctx)) grouped.set(ctx, []);
      grouped.get(ctx)!.push(file);
    }

    // Order by CONTEXT_OPTIONS order
    for (const opt of CONTEXT_OPTIONS) {
      const ctxFiles = grouped.get(opt.value);
      if (ctxFiles && ctxFiles.length > 0) {
        groups.push({ context: opt.value, label: opt.label, files: ctxFiles });
        grouped.delete(opt.value);
      }
    }
    // Any remaining unknown contexts
    for (const [ctx, ctxFiles] of grouped) {
      groups.push({ context: ctx, label: ctx, files: ctxFiles });
    }

    return groups;
  }, [files]);

  const toggleGroup = (context: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(context)) next.delete(context);
      else next.add(context);
      return next;
    });
  };

  const handleFolderClick = () => {
    // Reset input so same folder can be re-selected
    if (folderInputRef.current) folderInputRef.current.value = '';
    folderInputRef.current?.click();
  };

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    setSelectedFiles(Array.from(fileList));
    setUploadErrors([]);
    setShowModal(true);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploadErrors([]);
    const errors: string[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      setUploadProgress({ current: i + 1, total: selectedFiles.length, name: file.name });

      try {
        await filesApi.uploadViaPresign(
          file,
          {
            subjectId,
            visitId: fileContext,
            visitName: CONTEXT_OPTIONS.find(o => o.value === fileContext)?.label || fileContext,
            category,
            description,
          },
          (pct) => setUploadProgress({ current: i + 1, total: selectedFiles.length, name: `${file.name} (${pct}%)` })
        );
      } catch (error) {
        errors.push(`${file.name}: ${(error as Error).message || 'Upload failed'}`);
      }
    }

    setUploadProgress(null);
    setUploadErrors(errors);
    queryClient.invalidateQueries({ queryKey: ['files', subjectId] });
    queryClient.invalidateQueries({ queryKey: ['subject-summary'] });

    if (errors.length === 0) {
      setLastUploadContext(fileContext);
      setShowModal(false);
      setSelectedFiles([]);
      setCategory('other');
      setDescription('');
      if (folderInputRef.current) folderInputRef.current.value = '';
      // Show follow-up dialog to add associated documents
      setShowDocsFollowUp(true);
      // Expand the group we just uploaded to
      setExpandedGroups(prev => new Set(prev).add(fileContext));
    }
  };

  const handleDownload = async (file: FileUpload) => {
    try {
      setDownloading(file.fileId);
      const { url } = await filesApi.getDownloadUrl(file.fileId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (file: FileUpload) => {
    if (!window.confirm(`Delete "${file.originalName}"? This cannot be undone.`)) return;
    try {
      setDeleting(file.fileId);
      await filesApi.delete(file.fileId);
      queryClient.invalidateQueries({ queryKey: ['files', subjectId] });
    queryClient.invalidateQueries({ queryKey: ['subject-summary'] });
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteGroup = async (group: { context: string; label: string; files: FileUpload[] }) => {
    if (!window.confirm(`Delete all ${group.files.length} files in "${group.label}"? This cannot be undone.`)) return;
    try {
      setDeletingGroup(group.context);
      await filesApi.deleteBatch(group.files.map(f => f.fileId));
      queryClient.invalidateQueries({ queryKey: ['files', subjectId] });
    queryClient.invalidateQueries({ queryKey: ['subject-summary'] });
    } catch (error) {
      console.error('Batch delete error:', error);
      alert(`Failed to delete: ${(error as Error).message}`);
    } finally {
      setDeletingGroup(null);
    }
  };

  const handleDownloadGroup = async (group: { context: string; label: string; files: FileUpload[] }) => {
    try {
      setDownloadingGroup(group.context);
      // Single server-side ZIP of this subject's files for this visit context
      // (one clean download instead of N separate browser downloads).
      await filesApi.downloadSubject(subjectId, group.context);
    } catch (error) {
      console.error('Batch download error:', error);
      alert(`Failed to download: ${(error as Error).message}`);
    } finally {
      setDownloadingGroup(null);
    }
  };

  // Check if user can delete an entire group (must own all files or be admin)
  const canDeleteGroup = (group: { files: FileUpload[] }) => {
    if (user?.role === 'admin') return true;
    return group.files.every(f => f.uploadedBy?.username === user?.username);
  };

  // Handle document file selection (for follow-up or add-to-group)
  const handleDocSelect = (e: React.ChangeEvent<HTMLInputElement>, source: 'followup' | 'group') => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    setDocFiles(Array.from(fileList));
    if (source === 'followup') {
      // Files selected from follow-up dialog — stay in that dialog
    }
  };

  // Upload documents to a specific context
  const handleDocUpload = async (targetContext: string) => {
    if (docFiles.length === 0) return;

    const errors: string[] = [];
    for (let i = 0; i < docFiles.length; i++) {
      const file = docFiles[i];
      setDocUploadProgress({ current: i + 1, total: docFiles.length, name: file.name });
      try {
        await filesApi.uploadViaPresign(
          file,
          {
            subjectId,
            visitId: targetContext,
            visitName: CONTEXT_OPTIONS.find(o => o.value === targetContext)?.label || targetContext,
            category: docCategory,
            description: docDescription,
          },
          (pct) => setDocUploadProgress({ current: i + 1, total: docFiles.length, name: `${file.name} (${pct}%)` })
        );
      } catch (error) {
        errors.push(`${file.name}: ${(error as Error).message || 'Upload failed'}`);
      }
    }

    setDocUploadProgress(null);
    queryClient.invalidateQueries({ queryKey: ['files', subjectId] });
    queryClient.invalidateQueries({ queryKey: ['subject-summary'] });

    if (errors.length === 0) {
      setDocFiles([]);
      setDocCategory('tws');
      setDocDescription('');
      setShowDocsFollowUp(false);
      setAddDocsToGroup(null);
      setShowDocsTopLevel(false);
      if (docInputRef.current) docInputRef.current.value = '';
      if (followUpDocInputRef.current) followUpDocInputRef.current.value = '';
      if (topDocInputRef.current) topDocInputRef.current.value = '';
      setExpandedGroups(prev => new Set(prev).add(targetContext));
    } else {
      alert(`${errors.length} file(s) failed to upload:\n${errors.slice(0, 3).join('\n')}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Loading files...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Hidden folder input */}
      <input
        ref={folderInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is not in the standard types
        webkitdirectory=""
        directory=""
        multiple
        onChange={handleFolderSelect}
        className="hidden"
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          Files ({files.length})
        </h4>
        {canUpload && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setDocFiles([]); setDocCategory('other'); setDocDescription(''); setTopDocContext('PROC'); setShowDocsTopLevel(true); }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
              title="Upload individual documents (reports, worksheets, source docs)"
            >
              <FileText className="w-3.5 h-3.5" />
              Upload Documents
            </button>
            <button
              onClick={handleFolderClick}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              title="Upload an imaging study as a folder of images"
            >
              <FolderUp className="w-3.5 h-3.5" />
              Upload Images
            </button>
          </div>
        )}
      </div>

      {/* Persistent PHI de-identification notice (always visible when uploads are allowed) */}
      {canUpload && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg">
          <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <span className="font-semibold">Upload de-identified files only.</span> Remove PHI before uploading — no patient
            name, MRN, or date of birth in the image overlay, and strip PHI from DICOM headers. Label with the study Subject ID.
          </p>
        </div>
      )}

      {/* Upload modal — appears after folder is selected */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Upload Images</h3>
              <button onClick={() => { setShowModal(false); setSelectedFiles([]); setUploadProgress(null); setUploadErrors([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
              <FolderUp className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-800 font-medium">
                {selectedFiles.length} images selected ({formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))})
              </span>
            </div>

            {/* PHI / de-identification warning */}
            <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-300 rounded-lg">
              <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Confirm images are de-identified before uploading.</p>
                <p className="mt-0.5 text-amber-700">
                  Remove all PHI — patient name, MRN, and date of birth in the image overlay, and PHI stripped from DICOM
                  headers. Only anonymized images may be uploaded. Use the study Subject ID, not patient identifiers.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">When were these images taken?</label>
              <select
                value={fileContext}
                onChange={(e) => setFileContext(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
              >
                {CONTEXT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Imaging Modality</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Pre-procedure angiogram"
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
              />
            </div>

            {/* Progress bar */}
            {uploadProgress && (
              <div className="space-y-1">
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-blue-700">
                  Uploading {uploadProgress.current}/{uploadProgress.total}: {uploadProgress.name}
                </p>
              </div>
            )}

            {/* Errors */}
            {uploadErrors.length > 0 && (
              <div className="text-xs text-red-600 space-y-0.5 bg-red-50 rounded-lg p-3">
                <p className="font-medium">{uploadErrors.length} file{uploadErrors.length !== 1 ? 's' : ''} failed:</p>
                {uploadErrors.slice(0, 5).map((err, i) => (
                  <p key={i}>{err}</p>
                ))}
                {uploadErrors.length > 5 && (
                  <p>...and {uploadErrors.length - 5} more</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowModal(false); setSelectedFiles([]); setUploadProgress(null); setUploadErrors([]); }}
                disabled={!!uploadProgress}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!!uploadProgress}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {uploadProgress ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                ) : (
                  <><FolderUp className="w-4 h-4" /> Upload {selectedFiles.length} Images</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden doc file inputs */}
      <input
        ref={followUpDocInputRef}
        type="file"
        multiple
        onChange={(e) => handleDocSelect(e, 'followup')}
        className="hidden"
      />
      <input
        ref={docInputRef}
        type="file"
        multiple
        onChange={(e) => handleDocSelect(e, 'group')}
        className="hidden"
      />
      <input
        ref={topDocInputRef}
        type="file"
        multiple
        onChange={(e) => handleDocSelect(e, 'group')}
        className="hidden"
      />

      {/* Follow-up dialog after folder upload */}
      {showDocsFollowUp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Upload Complete</h3>
              <button onClick={() => { setShowDocsFollowUp(false); setDocFiles([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Do you have associated documents (TWS worksheets, reports, etc.) to add to <span className="font-medium">{CONTEXT_OPTIONS.find(o => o.value === lastUploadContext)?.label}</span>?
            </p>

            {docFiles.length === 0 ? (
              <button
                onClick={() => { if (followUpDocInputRef.current) followUpDocInputRef.current.value = ''; followUpDocInputRef.current?.click(); }}
                className="flex items-center gap-2 px-4 py-3 w-full border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Select documents to add
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800 font-medium">
                    {docFiles.length} document{docFiles.length !== 1 ? 's' : ''} selected ({formatFileSize(docFiles.reduce((sum, f) => sum + f.size, 0))})
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={docCategory}
                    onChange={(e) => setDocCategory(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={docDescription}
                    onChange={(e) => setDocDescription(e.target.value)}
                    placeholder="e.g., TWS worksheet"
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {docUploadProgress && (
                  <div className="space-y-1">
                    <div className="w-full bg-green-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(docUploadProgress.current / docUploadProgress.total) * 100}%` }} />
                    </div>
                    <p className="text-xs text-green-700">Uploading {docUploadProgress.current}/{docUploadProgress.total}: {docUploadProgress.name}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowDocsFollowUp(false); setDocFiles([]); }}
                disabled={!!docUploadProgress}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                {docFiles.length === 0 ? 'No, Skip' : 'Cancel'}
              </button>
              {docFiles.length > 0 && (
                <button
                  onClick={() => handleDocUpload(lastUploadContext)}
                  disabled={!!docUploadProgress}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {docUploadProgress ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Upload {docFiles.length} Document{docFiles.length !== 1 ? 's' : ''}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top-level: upload individual documents (visit + description only) */}
      {showDocsTopLevel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Upload Documents</h3>
              <button onClick={() => { setShowDocsTopLevel(false); setDocFiles([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Upload individual documents (reports, worksheets, source docs). For imaging studies, use <span className="font-medium">Upload Images</span> instead.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visit</label>
              <select
                value={topDocContext}
                onChange={(e) => setTopDocContext(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
              >
                {CONTEXT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {docFiles.length === 0 ? (
              <button
                onClick={() => { if (topDocInputRef.current) topDocInputRef.current.value = ''; topDocInputRef.current?.click(); }}
                className="flex items-center gap-2 px-4 py-3 w-full border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Select documents to upload
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800 font-medium">
                    {docFiles.length} document{docFiles.length !== 1 ? 's' : ''} selected ({formatFileSize(docFiles.reduce((sum, f) => sum + f.size, 0))})
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={docDescription}
                    onChange={(e) => setDocDescription(e.target.value)}
                    placeholder="e.g., Lab report"
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {docUploadProgress && (
                  <div className="space-y-1">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(docUploadProgress.current / docUploadProgress.total) * 100}%` }} />
                    </div>
                    <p className="text-xs text-blue-700">Uploading {docUploadProgress.current}/{docUploadProgress.total}: {docUploadProgress.name}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowDocsTopLevel(false); setDocFiles([]); }}
                disabled={!!docUploadProgress}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              {docFiles.length > 0 && (
                <button
                  onClick={() => handleDocUpload(topDocContext)}
                  disabled={!!docUploadProgress}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {docUploadProgress ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Upload {docFiles.length} Document{docFiles.length !== 1 ? 's' : ''}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add documents to existing group modal */}
      {addDocsToGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                Add Documents — {CONTEXT_OPTIONS.find(o => o.value === addDocsToGroup)?.label || addDocsToGroup}
              </h3>
              <button onClick={() => { setAddDocsToGroup(null); setDocFiles([]); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {docFiles.length === 0 ? (
              <button
                onClick={() => { if (docInputRef.current) docInputRef.current.value = ''; docInputRef.current?.click(); }}
                className="flex items-center gap-2 px-4 py-3 w-full border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Select files to add
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800 font-medium">
                    {docFiles.length} file{docFiles.length !== 1 ? 's' : ''} selected ({formatFileSize(docFiles.reduce((sum, f) => sum + f.size, 0))})
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <select
                    value={docCategory}
                    onChange={(e) => setDocCategory(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={docDescription}
                    onChange={(e) => setDocDescription(e.target.value)}
                    placeholder="e.g., TWS worksheet"
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {docUploadProgress && (
                  <div className="space-y-1">
                    <div className="w-full bg-green-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(docUploadProgress.current / docUploadProgress.total) * 100}%` }} />
                    </div>
                    <p className="text-xs text-green-700">Uploading {docUploadProgress.current}/{docUploadProgress.total}: {docUploadProgress.name}</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setAddDocsToGroup(null); setDocFiles([]); }}
                disabled={!!docUploadProgress}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              {docFiles.length > 0 && (
                <button
                  onClick={() => handleDocUpload(addDocsToGroup)}
                  disabled={!!docUploadProgress}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {docUploadProgress ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Upload {docFiles.length} Document{docFiles.length !== 1 ? 's' : ''}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File list grouped by context */}
      {files.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-2">No files uploaded</p>
      ) : (
        <div className="space-y-2">
          {fileGroups.map((group) => {
            const isExpanded = expandedGroups.has(group.context);
            const totalSize = group.files.reduce((sum, f) => sum + f.fileSize, 0);

            return (
              <div key={group.context} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Accordion header */}
                <div className="relative">
                <button
                  onClick={() => toggleGroup(group.context)}
                  className="w-full flex items-center justify-between pl-4 pr-44 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="text-sm font-medium text-gray-800">{group.label}</span>
                    <span className="text-xs text-gray-500">
                      ({group.files.length} file{group.files.length !== 1 ? 's' : ''} — {formatFileSize(totalSize)})
                    </span>
                  </div>
                  <span />
                </button>
                {/* Header action buttons */}
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownloadGroup(group); }}
                    disabled={downloadingGroup === group.context}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title={`Download these ${group.files.length} files`}
                  >
                    {downloadingGroup === group.context ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><Download className="w-3.5 h-3.5" /> Download ({group.files.length})</>
                    )}
                  </button>
                  {canUpload && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDocFiles([]); setDocCategory('tws'); setDocDescription(''); setAddDocsToGroup(group.context); }}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Add documents to this group"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Files
                    </button>
                  )}
                  {canDeleteGroup(group) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }}
                      disabled={deletingGroup === group.context}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                      title={`Delete all ${group.files.length} files`}
                    >
                      {deletingGroup === group.context ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><Trash2 className="w-3.5 h-3.5" /> Delete All</>
                      )}
                    </button>
                  )}
                </div>
                </div>

                {/* Accordion body with Images / Documents tabs */}
                {isExpanded && (() => {
                  const images = group.files.filter(f => f.fileType !== 'pdf' && f.category !== 'tws');
                  const documents = group.files.filter(f => f.fileType === 'pdf' || f.category === 'tws');
                  const currentTab = activeTab[group.context] || 'images';

                  return (
                    <div>
                      {/* Tab bar */}
                      <div className="flex border-b border-gray-200 px-4">
                        <button
                          onClick={() => setActiveTab(prev => ({ ...prev, [group.context]: 'images' }))}
                          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                            currentTab === 'images'
                              ? 'border-blue-600 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Images ({images.length})
                        </button>
                        <button
                          onClick={() => setActiveTab(prev => ({ ...prev, [group.context]: 'documents' }))}
                          className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                            currentTab === 'documents'
                              ? 'border-blue-600 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Documents ({documents.length})
                        </button>
                      </div>

                      {/* Tab content */}
                      <div className="divide-y divide-gray-100">
                        {(currentTab === 'images' ? images : documents).length === 0 ? (
                          <p className="text-xs text-gray-400 italic py-3 px-4">
                            No {currentTab} uploaded
                          </p>
                        ) : (
                          (currentTab === 'images' ? images : documents).map((file) => (
                            <div key={file.fileId} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {getFileIcon(file.fileType)}
                                <div className="min-w-0">
                                  <p className="text-sm text-gray-900 truncate">
                                    {file.originalName}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {formatFileSize(file.fileSize)}
                                    {file.category && <span className="ml-1 capitalize">— {file.category}</span>}
                                    {' — '}{file.uploadedBy?.fullName || 'Unknown'}
                                    {' — '}{new Date(file.uploadedAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                <button
                                  onClick={() => handleDownload(file)}
                                  disabled={downloading === file.fileId}
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                  title="Download"
                                >
                                  {downloading === file.fileId ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </button>
                                {canDelete(file) && (
                                  <button
                                    onClick={() => handleDelete(file)}
                                    disabled={deleting === file.fileId}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                    title="Delete"
                                  >
                                    {deleting === file.fileId ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
